// Argus — File Explorer toolbar IconButton menu (fix #4 din feedback iter2 round 2).
// Conținut:
//   Show folder tree | Show preview pane | Divider |
//   View › Details / List / Thumbnails / Large thumbnails |
//   Sort by › File name / Size / Modified at / Divider / Ascending / Descending |
//   Divider | Options (modal Settings file explorer) | Divider | Properties (drive/folder curent)
//
// Optiunile curente se marcheaza cu CheckIcon (pattern aliniat WebcamWindow ⚙️ menu).

import { useState } from 'react';
import {
  IconButton,
  Menu,
  MenuItem,
  Divider,
  ListItemIcon,
  ListItemText,
  Tooltip,
} from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import CheckIcon from '@mui/icons-material/Check';
import ArrowRightIcon from '@mui/icons-material/ArrowRight';
import type { ViewMode } from './DirectoryContent';

import type { SortKey, SortDir } from './DirectoryContent';
export type { SortKey, SortDir };

export interface ToolbarMenuProps {
  treeOpen: boolean;
  previewOpen: boolean;
  viewMode: ViewMode;
  sortKey: SortKey;
  sortDir: SortDir;
  hasCurrentEntry: boolean; // true dacă are sens Properties (drive/folder curent)
  onToggleTree: () => void;
  onTogglePreview: () => void;
  onChangeView: (mode: ViewMode) => void;
  onChangeSortKey: (key: SortKey) => void;
  onChangeSortDir: (dir: SortDir) => void;
  onOpenOptions: () => void;
  onOpenProperties: () => void;
}

const VIEW_LABELS: Array<[ViewMode, string]> = [
  ['details', 'Details'],
  ['list', 'List'],
  ['thumbnails', 'Thumbnails'],
  ['largeThumbnails', 'Large thumbnails'],
];

const SORT_KEY_LABELS: Array<[SortKey, string]> = [
  ['name', 'File name'],
  ['size', 'Size'],
  ['modifiedAt', 'Modified at'],
];

export function ToolbarMenu(props: ToolbarMenuProps): JSX.Element {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [viewSubAnchor, setViewSubAnchor] = useState<null | HTMLElement>(null);
  const [sortSubAnchor, setSortSubAnchor] = useState<null | HTMLElement>(null);
  const open = anchorEl !== null;

  const handleOpen = (e: React.MouseEvent<HTMLElement>) => setAnchorEl(e.currentTarget);
  const handleClose = () => {
    setAnchorEl(null);
    setViewSubAnchor(null);
    setSortSubAnchor(null);
  };

  return (
    <>
      <Tooltip title="More">
        <IconButton size="small" onClick={handleOpen}>
          <MoreVertIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <Menu anchorEl={anchorEl} open={open} onClose={handleClose} slotProps={{ list: { dense: true } }}>
        <MenuItem onClick={() => { props.onToggleTree(); handleClose(); }}>
          <ListItemIcon>{props.treeOpen ? <CheckIcon fontSize="small" /> : null}</ListItemIcon>
          <ListItemText>Show folder tree</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => { props.onTogglePreview(); handleClose(); }}>
          <ListItemIcon>{props.previewOpen ? <CheckIcon fontSize="small" /> : null}</ListItemIcon>
          <ListItemText>Show preview pane</ListItemText>
        </MenuItem>

        <Divider />

        <MenuItem onClick={(e) => setViewSubAnchor(e.currentTarget)}>
          <ListItemIcon><ArrowRightIcon fontSize="small" /></ListItemIcon>
          <ListItemText>View</ListItemText>
        </MenuItem>
        <Menu
          anchorEl={viewSubAnchor}
          open={viewSubAnchor !== null}
          onClose={() => setViewSubAnchor(null)}
          anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'left' }}
          slotProps={{ list: { dense: true } }}
        >
          {VIEW_LABELS.map(([mode, label]) => (
            <MenuItem key={mode} onClick={() => { props.onChangeView(mode); setViewSubAnchor(null); handleClose(); }}>
              <ListItemIcon>{props.viewMode === mode ? <CheckIcon fontSize="small" /> : null}</ListItemIcon>
              <ListItemText>{label}</ListItemText>
            </MenuItem>
          ))}
        </Menu>

        <MenuItem onClick={(e) => setSortSubAnchor(e.currentTarget)}>
          <ListItemIcon><ArrowRightIcon fontSize="small" /></ListItemIcon>
          <ListItemText>Sort by</ListItemText>
        </MenuItem>
        <Menu
          anchorEl={sortSubAnchor}
          open={sortSubAnchor !== null}
          onClose={() => setSortSubAnchor(null)}
          anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'left' }}
          slotProps={{ list: { dense: true } }}
        >
          {SORT_KEY_LABELS.map(([key, label]) => (
            <MenuItem key={key} onClick={() => { props.onChangeSortKey(key); setSortSubAnchor(null); handleClose(); }}>
              <ListItemIcon>{props.sortKey === key ? <CheckIcon fontSize="small" /> : null}</ListItemIcon>
              <ListItemText>{label}</ListItemText>
            </MenuItem>
          ))}
          <Divider />
          <MenuItem onClick={() => { props.onChangeSortDir('asc'); setSortSubAnchor(null); handleClose(); }}>
            <ListItemIcon>{props.sortDir === 'asc' ? <CheckIcon fontSize="small" /> : null}</ListItemIcon>
            <ListItemText>Ascending</ListItemText>
          </MenuItem>
          <MenuItem onClick={() => { props.onChangeSortDir('desc'); setSortSubAnchor(null); handleClose(); }}>
            <ListItemIcon>{props.sortDir === 'desc' ? <CheckIcon fontSize="small" /> : null}</ListItemIcon>
            <ListItemText>Descending</ListItemText>
          </MenuItem>
        </Menu>

        <Divider />
        <MenuItem onClick={() => { props.onOpenOptions(); handleClose(); }}>
          <ListItemIcon /><ListItemText>Options…</ListItemText>
        </MenuItem>
        <Divider />
        <MenuItem disabled={!props.hasCurrentEntry} onClick={() => { props.onOpenProperties(); handleClose(); }}>
          <ListItemIcon /><ListItemText>Properties</ListItemText>
        </MenuItem>
      </Menu>
    </>
  );
}
